import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { formatStatusLabel, getStatusModifier } from '../utils/status';

const StatusTimeline = ({ samples }) => {
    const segments = useMemo(() => buildSegments(samples), [samples]);
    if (!segments.length) {
        return (
            <div className="status-timeline status-timeline--empty" aria-live="polite">
                No historical status data available for the selected range.
            </div>
        );
    }
    return (
        <div className="status-timeline" role="list">
            {segments.map((segment) => (
                <div
                    key={`${segment.status}-${segment.start.getTime()}`}
                    className={[
                        'status-timeline__segment',
                        `status-timeline__segment--${segment.modifier}`,
                    ].join(' ')}
                    style={{ flexGrow: segment.weight }}
                    role="listitem"
                    aria-label={`${formatStatusLabel(segment.status)} from ${segment.startLabel} to ${segment.endLabel}`}
                    title={`${formatStatusLabel(segment.status)} from ${segment.startLabel} to ${segment.endLabel}`}
                >
                    {segment.showLabel && (
                        <span className="status-timeline__label">{formatStatusLabel(segment.status)}</span>
                    )}
                </div>
            ))}
        </div>
    );
};

StatusTimeline.propTypes = {
    samples: PropTypes.arrayOf(PropTypes.shape({
        timestamp: PropTypes.instanceOf(Date).isRequired,
        status: PropTypes.string,
    })),
};

StatusTimeline.defaultProps = {
    samples: [],
};

const buildSegments = (samples) => {
    const valid = (samples || [])
        .filter((sample) => sample && sample.timestamp instanceof Date && !Number.isNaN(sample.timestamp.getTime()))
        .sort((a, b) => a.timestamp - b.timestamp);
    if (!valid.length) {
        return [];
    }
    const formatter = new Intl.DateTimeFormat(undefined, {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
    const firstTime = valid[0].timestamp.getTime();
    const lastTime = valid[valid.length - 1].timestamp.getTime();
    const totalRange = Math.max(1, lastTime - firstTime);

    const segments = [];
    let current = {
        status: valid[0].status || 'UNKNOWN',
        start: valid[0].timestamp,
        end: valid[0].timestamp,
    };

    for (let i = 1; i < valid.length; i += 1) {
        const sample = valid[i];
        const status = sample.status || 'UNKNOWN';
        if (status === current.status) {
            current.end = sample.timestamp;
        } else {
            segments.push({ ...current });
            current = { status, start: sample.timestamp, end: sample.timestamp };
        }
    }
    segments.push({ ...current });

    return segments.map((segment) => {
        const startTime = segment.start.getTime();
        const endTime = Math.max(startTime, segment.end.getTime());
        const weight = Math.max(1e-6, (endTime - startTime) / totalRange);
        const modifier = getStatusModifier(segment.status);
        const startLabel = formatter.format(segment.start);
        const endLabel = formatter.format(segment.end);
        const showLabel = weight > 0.18;
        return {
            ...segment,
            weight,
            modifier,
            startLabel,
            endLabel,
            showLabel,
        };
    });
};

export default StatusTimeline;
